import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { languageOptions } from 'src/features/profile/utils/constants';
import { LangFormSchemaValues, langFormSchema } from 'src/features/profile/utils/formSchema';
import { settingsMessages } from 'src/features/profile/utils/messages';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockSubtitle,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { LOCALE } from 'src/providers/types';
import { generalMessages } from 'src/utils/messages';
import { storage } from 'src/utils/storage';

const ProfileSettingsForm = () => {
    const { formatMessage } = useIntl();
    const currentLang = storage.getLanguage()?.split('-')[0] || LOCALE.BG;

    const form = useForm<LangFormSchemaValues>({
        resolver: zodResolver(langFormSchema),
        defaultValues: { lang: currentLang },
    });

    const onSubmit = ({ lang }: LangFormSchemaValues) => {
        storage.setLanguage(lang);
        form.reset({ lang });
        window.location.reload();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(settingsMessages.language)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>
                        {formatMessage(settingsMessages.languageSubtitle)}
                    </FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="lang"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger>
                                            <SelectValue
                                                placeholder={formatMessage(settingsMessages.changeLanguagePlaceholder)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(Object.values(LOCALE) as LOCALE[]).map((language) => (
                                                <SelectItem key={language} value={language}>
                                                    {formatMessage(languageOptions[language])}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <div className="flex justify-end">
                <Button type="submit" disabled={!form.formState.isDirty}>
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default ProfileSettingsForm;
